import React, { FC, useEffect } from 'react'
import format from 'date-fns/format'
import ja from 'date-fns/locale/ja'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { CautionCard } from 'components/Card'
import { dateToString } from 'lib/helper'
import { fetchStocks, initStocks, unsubscribeStocks } from 'reducks/stock/operations'
import { StockSettingSection } from './index'
import { fetchSchedules } from 'reducks/schedule/operations'
import { getStoreRef } from 'reducks/auth/selectors'
import { fetchDailyOrders, initOrders, unsubscribeOrders } from 'reducks/checkout/operations'
import { getStocks } from 'reducks/stock/selectors'
import { getModalOpen } from '../../reducks/modal/selectors'

type Props = {
  isAfterLastOrder: boolean
  isBeforeOrder: boolean
  isClosed: boolean
  isOrderable: boolean
  nextBusinessDate: Date
  page: 'released' | 'unreleased'
}

const StockContainer: FC<Props> = React.memo((props) => {
  const { isAfterLastOrder, isBeforeOrder, isClosed, isOrderable, nextBusinessDate, page } = props
  const dispatch = useDispatch()

  const isModalOpen = useSelector((state: State) => getModalOpen(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const stocks = useSelector((state: State) => getStocks(state))
  const now = new Date()

  useEffect(() => {
    // 臨時営業日設定モーダルが閉じられたら在庫を再取得する
    if (!storeRef || isModalOpen) return
    dispatch(initStocks())
    // ラストオーダー以降または休業日の場合、翌営業日の在庫情報を取得
    dispatch(
      fetchStocks({
        date: isAfterLastOrder || isClosed ? dateToString(nextBusinessDate) : dateToString(now),
        types: ['daily'],
        storeRef,
      })
    )
    return () => {
      dispatch(unsubscribeStocks())
    }
  }, [storeRef, page, isAfterLastOrder, isClosed, isModalOpen])

  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchSchedules(storeRef, ['daily']))
  }, [storeRef])

  useEffect(() => {
    if (!storeRef) return
    dispatch(initOrders())
    // ラストオーダー以降または休業日の場合、翌営業日の注文情報を取得
    dispatch(
      fetchDailyOrders(isAfterLastOrder || isClosed ? dateToString(nextBusinessDate) : dateToString(now), storeRef, [
        'succeeded',
      ])
    )
    return () => {
      dispatch(unsubscribeOrders())
    }
  }, [storeRef, page, isAfterLastOrder, isClosed])

  // 販売中ページで在庫が存在しない場合
  if (stocks.length === 0 && page === 'released') {
    return (
      <CautionCard
        caution={'在庫が設定されている商品は\nありません'}
        description={'未販売の商品から、在庫を追加してください。'}
      />
    )
  } else {
    // 営業時間内
    if (isOrderable && !isClosed) {
      return <StockSettingSection page={page} stocks={stocks} />
      // 休業日またはオーダー開始時刻まで
    } else if (isBeforeOrder || isClosed) {
      return (
        <>
          <CautionCard
            caution={`次回${format(isAfterLastOrder || isClosed ? nextBusinessDate : now, 'M月d日(E)', {
              locale: ja,
            })}のオープン時に予定されている在庫です`}
            description={'在庫数を変更する場合は商品をタップして編集してください。'}
          />
          <StockSettingSection page={page} stocks={stocks} />
        </>
      )
    } else {
      return <></>
    }
  }
})

export default StockContainer
